/**
 * Compute opportunity counts per country from a caller-supplied dataset.
 * Structured as pure functions so the calling component can pass in the
 * live list from the API without any module-level side effects.
 */
export const computeCountryCounts = (opportunities = []) => {
  const counts = {};
  for (const opp of opportunities) {
    counts[opp.country] = (counts[opp.country] ?? 0) + 1;
  }
  return counts;
};

/**
 * Format an integer using compact notation.
 *   38          -> "38"
 *   467         -> "467"
 *   1200        -> "1.2K"
 *   12400       -> "12.4K"
 *   1_000_000   -> "1M"
 */
export const formatCompactCount = (n) => {
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const v = n / 1000;
    const s = v >= 10 ? v.toFixed(1) : v.toFixed(1);
    return `${s.replace(/\.0$/, "")}K`;
  }
  const v = n / 1_000_000;
  return `${v.toFixed(v >= 10 ? 0 : 1).replace(/\.0$/, "")}M`;
};

/**
 * Return countries sorted by (count desc, displayOrder asc, name asc).
 * Countries with zero opportunities are included by default.
 */
export const getSortedCountriesWithCounts = (countries, opportunities, { includeEmpty = true } = {}) => {
  const counts = computeCountryCounts(opportunities);
  const withCounts = (countries || []).map((c) => ({
    ...c,
    count: counts[c.name] ?? 0,
  }));
  const filtered = includeEmpty ? withCounts : withCounts.filter((c) => c.count > 0);
  return filtered.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    const orderA = a.displayOrder ?? 9999;
    const orderB = b.displayOrder ?? 9999;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name, "ar");
  });
};
